/**
 * useAdminNoteAuditsList Hook
 *
 * Fetches and manages note audits list for admin view
 * Supports filtering by organization
 */

import { useMemo } from 'react';

import { useGetAdminGlobalNotesQuery, useGetAdminOrganizationNotesQuery } from '../../store/api';
import type { NoteAuditListItem } from '../../types';

interface UseAdminNoteAuditsListOptions {
  /** Filter by specific organization ID */
  organizationId?: string;
}

interface UseAdminNoteAuditsListResult {
  /** List of note audits */
  audits: NoteAuditListItem[];
  /** Total number of audits */
  total: number;
  /** Current page */
  page: number;
  /** Items per page */
  perPage: number;
  /** Whether there are more items */
  hasMore: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useAdminNoteAuditsList = (
  options: UseAdminNoteAuditsListOptions = {},
): UseAdminNoteAuditsListResult => {
  const { organizationId } = options;

  // Use org-specific query if organizationId is provided, otherwise use global
  const globalQuery = useGetAdminGlobalNotesQuery(undefined, {
    skip: !!organizationId,
  });

  const orgQuery = useGetAdminOrganizationNotesQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId },
  );

  const { data, isLoading, error, refetch: refetchQuery } = organizationId ? orgQuery : globalQuery;

  const refetch = (): void => {
    void refetchQuery();
  };

  const audits = useMemo(() => data?.audits || [], [data]);
  const total = data?.total || 0;
  const page = data?.page || 1;
  const perPage = data?.per_page || 20;
  const hasMore = data?.has_more || false;
  const isEmpty = !isLoading && audits.length === 0;

  return {
    audits,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    error: error ? 'Failed to load note audits' : null,
    refetch,
    isEmpty,
  };
};

export default useAdminNoteAuditsList;

